import React, { useEffect, useState } from 'react';
import {
  fetchGuestRequests,
  checkIn,
  requestRefund,
} from '@/modules/hosting/client/api/hosting.api';
import Avatar from '@/modules/users/client/components/Avatar.component';
import PropTypes from 'prop-types';
import InvoiceModal from './InvoiceModal.component';

// Main component that handles both Host and Guest views
export default function Trips({ currentUser, userRole = 'guest' }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, upcoming, past, cancelled
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInvoiceRequest, setSelectedInvoiceRequest] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const isHostView = userRole === 'host';

  useEffect(() => {
    async function loadRequests() {
      try {
        setLoading(true);
        const data = await fetchGuestRequests(currentUser._id);
        setRequests(data);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching requests:', error);
      } finally {
        setLoading(false);
      }
    }

    if (currentUser?._id) loadRequests();
  }, [currentUser]);

  // Filter requests based on status and search query
  const filteredRequests = requests.filter(request => {
    // Status filter
    if (filter !== 'all') {
      const statusMap = {
        upcoming: ['accepted', 'paid', 'checked_in', 'in_escrow'],
        past: ['completed', 'payout_released'],
        cancelled: ['rejected', 'refunded'],
      };
      if (!statusMap[filter].includes(request.status)) return false;
    }

    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      if (isHostView) {
        // Host view: search by guest name or location
        const guestName = `${request.guest?.firstName || ''}`.toLowerCase();
        const location = request.guest?.locationLiving?.toLowerCase() || '';
        return (
          guestName.includes(searchLower) || location.includes(searchLower)
        );
      } else {
        // Guest view: search by host name or destination
        const hostName = `${request.host?.firstName || ''} ${
          request.host?.lastName || ''
        }`.toLowerCase();
        const destination = request.destination?.toLowerCase() || '';
        return (
          hostName.includes(searchLower) || destination.includes(searchLower)
        );
      }
    }

    return true;
  });

  // Pagination logic
  const totalPages = Math.max(
    1,
    Math.ceil(filteredRequests.length / itemsPerPage),
  );
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRequests = filteredRequests.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  // Reset to page 1 when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchQuery, itemsPerPage]);

  // Calculate stats for sidebar
  const stats = {
    total: requests.length,
    upcoming: requests.filter(req =>
      ['accepted', 'paid', 'checked_in', 'in_escrow'].includes(req.status),
    ).length,
    past: requests.filter(req =>
      ['completed', 'payout_released'].includes(req.status),
    ).length,
    cancelled: requests.filter(req =>
      ['rejected', 'refunded'].includes(req.status),
    ).length,
  };

  const getStatusBadgeClass = status => {
    switch (status) {
      case 'accepted':
        return 'border-success text-success bg-success bg-opacity-10';
      case 'paid':
        return 'border-info text-info bg-info bg-opacity-10';
      case 'checked_in':
      case 'in_escrow':
        return 'border-primary text-primary bg-primary bg-opacity-10';
      case 'payout_released':
        return 'border-success text-success bg-success bg-opacity-10';
      case 'pending':
        return 'border-warning text-warning bg-warning bg-opacity-10';
      case 'rejected':
        return 'border-danger text-danger bg-danger bg-opacity-10';
      case 'refunded':
        return 'border-secondary text-secondary bg-secondary bg-opacity-10';
      case 'completed':
        return 'border-secondary text-secondary bg-secondary bg-opacity-10';
      default:
        return 'border-dark text-dark bg-dark bg-opacity-10';
    }
  };

  const getStatusText = status => {
    switch (status) {
      case 'accepted':
        return 'Confirmed';
      case 'paid':
        return 'Paid';
      case 'checked_in':
        return 'Checked In';
      case 'in_escrow':
        return 'In Escrow';
      case 'payout_released':
        return 'Payout Released';
      case 'pending':
        return 'Pending';
      case 'rejected':
        return 'Cancelled';
      case 'refunded':
        return 'Refunded';
      case 'completed':
        return 'Past';
      default:
        return status;
    }
  };

  const handleCheckIn = async requestId => {
    try {
      await checkIn(requestId);
      alert('Check-in successful! Funds are now in escrow.');
      // Reload requests
      const data = await fetchGuestRequests(currentUser._id);
      setRequests(data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error checking in:', error);
      alert(
        error.response?.data?.message ||
          'Failed to check in. Please try again.',
      );
    }
  };

  const handleRefund = async (requestId, reason) => {
    if (
      !window.confirm(
        'Are you sure you want to request a refund? This action may be subject to cancellation policy.',
      )
    ) {
      return;
    }

    try {
      await requestRefund(requestId, reason);
      alert('Refund request processed successfully.');
      // Reload requests
      const data = await fetchGuestRequests(currentUser._id);
      setRequests(data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error requesting refund:', error);
      alert(
        error.response?.data?.message ||
          'Failed to request refund. Please try again.',
      );
    }
  };

  return (
    <div className="container-spacer">
      <div className="container mt-4" style={{ width: 'fit-content' }}>
        {/* Header Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex align-items-center mb-3">
              <div className="bg-primary rounded-circle p-3 me-3">
                <i
                  className={`fas ${
                    isHostView ? 'fa-home' : 'fa-globe-americas'
                  } text-white fs-4`}
                ></i>
              </div>
              <div>
                <h1 className="h2 mb-1 fw-bold text-dark">
                  {isHostView ? 'My Guests' : 'My Trips'}
                </h1>
                <p className="text-muted mb-0">
                  {isHostView
                    ? 'Manage your guest requests and hosting interactions'
                    : 'Manage your trip requests and travel plans'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Main Content Column */}
          <div className="col-lg-8 col-12 mb-4">
            {/* Filters and Search */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="input-group input-group-lg">
                      <span className="input-group-text bg-light border-end-0 py-3 px-3">
                        <i className="fas fa-filter text-muted"></i>
                      </span>
                      <select
                        className="form-select border-start-0 py-3"
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                        style={{
                          cursor: 'pointer',
                          fontSize: '0.95rem',
                        }}
                      >
                        <option value="all">All Status</option>
                        <option value="upcoming">Upcoming</option>
                        <option value="past">Past</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      placeholder={
                        isHostView
                          ? 'Search by name or location...'
                          : 'Search by host name or city...'
                      }
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="col-md-2">
                    <button
                      className="btn btn-outline-secondary w-100"
                      onClick={() => {
                        setFilter('all');
                        setSearchQuery('');
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <div
                    className="spinner-border text-primary mb-3"
                    role="status"
                  ></div>
                  <h5 className="text-muted">
                    Loading {isHostView ? 'guests' : 'trips'}...
                  </h5>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!loading && filteredRequests.length === 0 && (
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <i className="fas fa-inbox fs-1 text-muted mb-3"></i>
                  <h5 className="text-muted">
                    No {isHostView ? 'Guests' : 'Trips'} Found
                  </h5>
                  <p className="text-muted mb-0">
                    {searchQuery || filter !== 'all'
                      ? 'Try adjusting your search or filters'
                      : `You don't have any ${
                          isHostView ? 'guest requests' : 'trips'
                        } yet.`}
                  </p>
                </div>
              </div>
            )}

            {/* Requests List */}
            {!loading && filteredRequests.length > 0 && (
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white py-3">
                  <h5 className="mb-0 fw-bold">
                    <i className="fas fa-list me-2 text-primary"></i>
                    {isHostView ? 'Guest Requests' : 'Host Profiles'}
                  </h5>
                </div>
                <div className="card-body p-0">
                  {currentRequests.map((request, index) => (
                    <div
                      key={request._id}
                      className={`border-bottom ${
                        index === currentRequests.length - 1
                          ? 'border-bottom-0'
                          : ''
                      }`}
                    >
                      <div className="p-4">
                        <div className="row align-items-start">
                          {/* Profile Image and Basic Info */}
                          <div className="col-md-2 col-3">
                            <Avatar
                              source="/img/avatar.png"
                              user={request?.host}
                              size={64}
                            />
                          </div>
                          <div className="col-md-6 col-9">
                            <div className="mb-2">
                              <h6 className="fw-bold mb-1">
                                {isHostView
                                  ? `${request.guest?.username || 'Guest'}`
                                  : `${request.host?.username || 'Host'}`}
                              </h6>
                              <div className="d-flex align-items-center text-muted small">
                                <i className="fas fa-star text-warning me-1"></i>
                                <i className="fas fa-map-marker-alt me-1"></i>
                                <span>
                                  {isHostView
                                    ? request.guest?.locationLiving ||
                                      'Unknown location'
                                    : request.host?.locationLiving ||
                                      'Unknown destination'}
                                </span>
                              </div>
                            </div>

                            <div className="d-flex align-items-center text-muted small mb-2">
                              <i className="fas fa-calendar me-2"></i>
                              <span>
                                {new Date(request.checkIn).toLocaleDateString(
                                  'en-GB',
                                )}{' '}
                                →{' '}
                                {new Date(request.checkOut).toLocaleDateString(
                                  'en-GB',
                                )}
                              </span>
                            </div>

                            {/* Message */}
                            {request.message && (
                              <div className="bg-light rounded p-2 mb-2">
                                <p className="mb-0 text-dark small fst-italic">
                                  {request.message}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Status and Actions */}
                          <div className="col-md-4 col-12 mt-3 mt-md-0">
                            <div className="d-flex flex-column h-100">
                              {/* Status Badge */}
                              <div className="mb-3">
                                <span
                                  className={`badge ${getStatusBadgeClass(
                                    request.status,
                                  )} px-3 py-2`}
                                  style={{
                                    fontSize: '0.85rem',
                                    borderRadius: '8px',
                                    display: 'inline-block',
                                  }}
                                >
                                  {getStatusText(request.status)}
                                </span>
                              </div>

                              {/* Status Messages */}
                              {!isHostView &&
                                request.status === 'accepted' &&
                                request.paymentStatus !== 'paid' && (
                                  <div
                                    className="text-muted small mb-3"
                                    style={{ fontSize: '0.8rem' }}
                                  >
                                    <i className="fas fa-info-circle me-1"></i>
                                    Waiting for payment
                                  </div>
                                )}
                              {request.status === 'in_escrow' && (
                                <div
                                  className="text-muted small mb-3"
                                  style={{ fontSize: '0.8rem' }}
                                >
                                  <i className="fas fa-lock me-1"></i>
                                  Funds in escrow
                                  {request.escrowReleaseDate && (
                                    <div className="mt-1">
                                      Release:{' '}
                                      {new Date(
                                        request.escrowReleaseDate,
                                      ).toLocaleDateString('en-GB')}
                                    </div>
                                  )}
                                </div>
                              )}
                              {request.status === 'payout_released' && (
                                <div className="text-success small mb-3">
                                  <i className="fas fa-check-circle me-1"></i>
                                  Payout released
                                </div>
                              )}
                              {request.status === 'refunded' && (
                                <div
                                  className="text-muted small mb-3"
                                  style={{ fontSize: '0.8rem' }}
                                >
                                  <i className="fas fa-undo me-1"></i>
                                  Refunded
                                </div>
                              )}

                              {/* Action Buttons */}
                              <div
                                className="d-flex flex-column gap-2 w-100"
                                style={{
                                  marginTop: 'auto',
                                }}
                              >
                                {/* Guest actions - Check-in and Refund buttons */}
                                {!isHostView && (
                                  <>
                                    {/* Check-in button - shows when paid and not already checked in */}
                                    {(request.status === 'paid' ||
                                      request.paymentStatus === 'paid') &&
                                      request.status !== 'in_escrow' &&
                                      request.status !== 'payout_released' &&
                                      request.status !== 'refunded' &&
                                      request.status !== 'checked_in' && (
                                        <button
                                          className="btn btn-success"
                                          onClick={() =>
                                            handleCheckIn(request._id)
                                          }
                                          style={{
                                            marginTop: '0.25rem',
                                            marginRight: '0.25rem',
                                            fontSize: '0.95rem',
                                            padding: '0.4rem 0.4rem',
                                            borderRadius: '6px',
                                            fontWeight: '500',
                                          }}
                                        >
                                          <i className="fas fa-sign-in-alt me-2"></i>
                                          Check In
                                        </button>
                                      )}
                                    {/* Refund button - shows when paid and not already refunded */}
                                    {(request.status === 'paid' ||
                                      request.paymentStatus === 'paid' ||
                                      (request.status === 'accepted' &&
                                        request.paymentStatus === 'paid')) &&
                                      request.status !== 'refunded' &&
                                      request.status !== 'in_escrow' &&
                                      request.status !== 'payout_released' && (
                                        <button
                                          className="btn btn-danger"
                                          onClick={() =>
                                            handleRefund(
                                              request._id,
                                              'Requested by guest',
                                            )
                                          }
                                          style={{
                                            fontSize: '0.95rem',
                                            padding: '0.4rem 0.4rem',
                                            borderRadius: '6px',
                                            fontWeight: '500',
                                          }}
                                        >
                                          <i className="fas fa-undo me-2"></i>
                                          Refund
                                        </button>
                                      )}
                                  </>
                                )}

                                {/* Message and Invoice buttons */}
                                <div className="d-flex gap-2 w-100">
                                  <a
                                    href={`/messages/${request.host?.username}?userId=${request.host?._id}&requestId=${request._id}`}
                                    className="btn btn-outline-primary flex-grow-1"
                                    style={{
                                      fontSize: '0.95rem',
                                      padding: '0.6rem 1rem',
                                      borderRadius: '6px',
                                      fontWeight: '500',
                                    }}
                                  >
                                    <i className="fas fa-comment me-2"></i>
                                    Message
                                  </a>
                                  {request.invoice && (
                                    <button
                                      className="btn btn-outline-secondary"
                                      onClick={() => {
                                        setSelectedInvoiceRequest(request);
                                        setShowInvoiceModal(true);
                                      }}
                                      style={{
                                        fontSize: '0.95rem',
                                        padding: '0.6rem 1rem',
                                        borderRadius: '6px',
                                        fontWeight: '500',
                                      }}
                                    >
                                      <i className="fas fa-file-invoice me-2"></i>
                                      Invoice
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Pagination */}
                {filteredRequests.length > 0 && (
                  <div className="card-footer bg-white border-top">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div className="d-flex align-items-center gap-3">
                        <div className="text-muted small">
                          Showing {indexOfFirstItem + 1} to{' '}
                          {Math.min(indexOfLastItem, filteredRequests.length)}{' '}
                          of {filteredRequests.length} result
                          {filteredRequests.length !== 1 ? 's' : ''}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <label className="text-muted small mb-0">Show:</label>
                          <select
                            className="form-select form-select-sm"
                            style={{ width: 'auto' }}
                            value={itemsPerPage}
                            onChange={e => {
                              setItemsPerPage(Number(e.target.value));
                              setCurrentPage(1);
                            }}
                          >
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                          </select>
                        </div>
                      </div>
                      <nav aria-label="Page navigation">
                        <ul className="pagination mb-0" style={{ gap: '4px' }}>
                          <li
                            className={`page-item ${
                              currentPage === 1 ? 'disabled' : ''
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(currentPage - 1)}
                              disabled={currentPage === 1}
                              style={{
                                minWidth: '90px',
                                border: '1px solid #dee2e6',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontWeight: '500',
                                transition: 'all 0.2s ease',
                                backgroundColor:
                                  currentPage === 1 ? '#f8f9fa' : '#fff',
                                color:
                                  currentPage === 1 ? '#6c757d' : '#495057',
                                cursor:
                                  currentPage === 1 ? 'not-allowed' : 'pointer',
                              }}
                              onMouseEnter={e => {
                                if (currentPage !== 1) {
                                  e.target.style.backgroundColor = '#e9ecef';
                                  e.target.style.borderColor = '#adb5bd';
                                }
                              }}
                              onMouseLeave={e => {
                                if (currentPage !== 1) {
                                  e.target.style.backgroundColor = '#fff';
                                  e.target.style.borderColor = '#dee2e6';
                                }
                              }}
                            >
                              <i className="fas fa-chevron-left me-1"></i>
                              Previous
                            </button>
                          </li>
                          {totalPages > 1 &&
                            Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(page => {
                                // Show first page, last page, current page, and pages around current
                                return (
                                  page === 1 ||
                                  page === totalPages ||
                                  (page >= currentPage - 1 &&
                                    page <= currentPage + 1)
                                );
                              })
                              .map((page, index, array) => {
                                // Add ellipsis if needed
                                const showEllipsisBefore =
                                  index > 0 && array[index - 1] < page - 1;
                                const isActive = currentPage === page;
                                return (
                                  <React.Fragment key={page}>
                                    {showEllipsisBefore && (
                                      <li className="page-item disabled">
                                        <span
                                          className="page-link"
                                          style={{
                                            border: 'none',
                                            backgroundColor: 'transparent',
                                            color: '#6c757d',
                                            padding: '8px 12px',
                                          }}
                                        >
                                          ...
                                        </span>
                                      </li>
                                    )}
                                    <li
                                      className={`page-item ${
                                        isActive ? 'active' : ''
                                      }`}
                                    >
                                      <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(page)}
                                        style={{
                                          minWidth: '40px',
                                          border: isActive
                                            ? '1px solid #0d6efd'
                                            : '1px solid #dee2e6',
                                          borderRadius: '6px',
                                          padding: '8px 12px',
                                          fontWeight: isActive ? '600' : '500',
                                          transition: 'all 0.2s ease',
                                          backgroundColor: isActive
                                            ? '#0d6efd'
                                            : '#fff',
                                          color: isActive ? '#fff' : '#495057',
                                          cursor: 'pointer',
                                        }}
                                        onMouseEnter={e => {
                                          if (!isActive) {
                                            e.target.style.backgroundColor =
                                              '#e9ecef';
                                            e.target.style.borderColor =
                                              '#adb5bd';
                                          }
                                        }}
                                        onMouseLeave={e => {
                                          if (!isActive) {
                                            e.target.style.backgroundColor =
                                              '#fff';
                                            e.target.style.borderColor =
                                              '#dee2e6';
                                          }
                                        }}
                                      >
                                        {page}
                                      </button>
                                    </li>
                                  </React.Fragment>
                                );
                              })}
                          <li
                            className={`page-item ${
                              currentPage === totalPages ? 'disabled' : ''
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              style={{
                                minWidth: '90px',
                                border: '1px solid #dee2e6',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontWeight: '500',
                                transition: 'all 0.2s ease',
                                backgroundColor:
                                  currentPage === totalPages
                                    ? '#f8f9fa'
                                    : '#fff',
                                color:
                                  currentPage === totalPages
                                    ? '#6c757d'
                                    : '#495057',
                                cursor:
                                  currentPage === totalPages
                                    ? 'not-allowed'
                                    : 'pointer',
                              }}
                              onMouseEnter={e => {
                                if (currentPage !== totalPages) {
                                  e.target.style.backgroundColor = '#e9ecef';
                                  e.target.style.borderColor = '#adb5bd';
                                }
                              }}
                              onMouseLeave={e => {
                                if (currentPage !== totalPages) {
                                  e.target.style.backgroundColor = '#fff';
                                  e.target.style.borderColor = '#dee2e6';
                                }
                              }}
                            >
                              Next
                              <i className="fas fa-chevron-right ms-1"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar Column */}
          <div className="col-lg-4 col-12">
            {/* Quick Stats Card */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white py-3">
                <h6 className="mb-0 fw-bold">
                  <i className="fas fa-chart-bar me-2 text-primary"></i>
                  Quick Stats
                </h6>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted">
                    {isHostView ? 'Total guests hosted' : 'Total trips taken: '}
                  </span>
                  <span className="fw-bold">{stats.total}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted fw-bold">Upcoming stays: </span>
                  <span className="fw-bold text-success">{stats.upcoming}</span>
                </div>
                {isHostView && (
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted">Response rate:</span>
                    <span className="fw-bold text-success">
                      {stats.total > 0
                        ? `${Math.round(
                            ((stats.upcoming + stats.past) / stats.total) * 100,
                          )}%`
                        : '0%'}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Tips Card */}
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h6 className="fw-bold mb-3">
                  <i className="fas fa-lightbulb me-2 text-warning"></i>
                  {isHostView ? 'Host Tips' : 'Travel Tips'}
                </h6>
                <p className="small text-muted mb-3">
                  {isHostView
                    ? 'Prepare guest room and local recommendations in advance for better hosting experience.'
                    : 'Pack essentials and confirm your travel arrangements before departure.'}
                </p>
                <div className="d-flex align-items-center text-muted small">
                  <i className="fas fa-clock me-2"></i>
                  <span>
                    Last updated: {new Date().toLocaleDateString('en-GB')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Modal */}
      <InvoiceModal
        show={showInvoiceModal}
        onHide={() => {
          setShowInvoiceModal(false);
          setSelectedInvoiceRequest(null);
        }}
        request={selectedInvoiceRequest}
        currentUser={currentUser}
      />
    </div>
  );
}

Trips.propTypes = {
  currentUser: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    username: PropTypes.string,
    locationLiving: PropTypes.string,
    avatarUploaded: PropTypes.bool,
    emailHash: PropTypes.string,
  }).isRequired,
  userRole: PropTypes.oneOf(['host', 'guest']),
};

Trips.defaultProps = {
  userRole: 'guest',
};
